import _useTranslation from 'lib/hooks/_useTranslation'
import { Body1, Body2 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled from 'styled-components'
import IssueSubjectIcon from 'assets/app/my_content/issue_subject.svg'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import Arrow from 'assets/icons/back-arrow.svg'
import SubjectSelection from 'components/modules/UploadDetails/SubjectSelection/SubjectSelection'
import { Form } from '@unform/web'
import { FormHandles } from '@unform/core/typings/types'
import { useRef, useState } from 'react'
import { Subject } from 'interfaces/Subject'
import { UpdateUserRequest } from 'interfaces/UpdateUserRequest'
import { toast } from 'react-toastify'
import { setUser } from 'redux/actions'
import UserService from 'services/UserService'
import { ApiError } from 'services/ApiService'
import { SubjectBadge } from 'components/elements/Badge'
import { useRouter } from 'next/router'
import { emptyFunction } from 'lib/utils'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AnalyticsEventOnboardingRegistrationSubjects } from 'lib/constants/AnalyticsEventType'
import { getReferrerScreen } from 'lib/formatting/url'
import { User } from 'interfaces/User'

const Container = styled.div<{ fullWidth?: boolean }>`
    border-radius: ${theme.borderRadius.webappCard};
    background-color: ${theme.colors.backgroundTransparent};
    padding: 15px;
    max-width: ${({ fullWidth }) => (fullWidth ? '100%' : '320px')};
    .layout {
        cursor: pointer;
        display: grid;
        grid-template-columns: auto 1fr auto;
        justify-content: center;
        gap: 15px;
        align-items: center;
        .texts {
            display: grid;
            gap: 5px;
            grid-template-columns: 1fr;
        }
        .subjects {
            display: flex;
            row-gap: 5px;
            flex-wrap: wrap;
            p {
                word-break: break-all;
                color: ${theme.colors.grayScaleSecondary};
            }
            & > span {
                height: fit-content !important;
                background-color: ${theme.colors.backgroundTransparent};
                border: none;
            }
        }
    }
`

const ArrowIcon = styled(Arrow)`
    transform: rotate(180deg);
    -ms-transform: rotate(180deg);
    -webkit-transform: rotate(180deg);
    fill: ${theme.colors.placeholderText} !important;
`

interface Props {
    forceShow?: boolean
    fullWidth?: boolean
    reloadHomepageFeed?: boolean
    setUser?: (user: User) => void
}

const IssueSubjectPicker = ({ reloadHomepageFeed, forceShow, fullWidth, setUser }: Props) => {
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const [issueSubjects, setIssueSubjects] = useState<Subject[]>(authenticatedUser?.issues ?? [])
    const formRef = useRef<FormHandles>(null)
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('app')
    const router = useRouter()
    const referrerScreen = getReferrerScreen(router)
    const isPreventAccessDialogShown = !authenticatedUser?.hasUsedApp

    const hasUserSelectedIssueSubjects = !issueSubjects.length

    const FormElement = () => (
        <div className="layout">
            <IssueSubjectIcon />
            <div className="texts">
                <Body1 fontFamily="var(--font-inter)" fontWeight={600} color={theme.colors.white}>
                    {t('app/yourIssueSubjectsLabel')}
                </Body1>

                {issueSubjects?.length ? (
                    <div className="subjects">
                        {issueSubjects.map((subject) => (
                            <SubjectBadge subject={subject} isSmall disallowLinking key={subject.id} />
                        ))}
                    </div>
                ) : (
                    <Body2 fontFamily="var(--font-inter)" fontWeight={400} color={theme.colors.grayScaleSecondary}>
                        {t('app/noIssueSubjectsLabel')}
                    </Body2>
                )}
            </div>
            <ArrowIcon />
        </div>
    )

    const onSave = (subjects: Subject[]) => {
        const newUser: UpdateUserRequest = {
            issueSubjectIds: subjects.map((s) => s.id),
        }

        UserService.update(newUser)
            .then((user) => {
                setUser && setUser(user)
                setIssueSubjects(user.issues)
                toast.success(t('app/subjectsInterestsUpdatedToastMessage'))
                trackEvent(AnalyticsEventOnboardingRegistrationSubjects, { referrerScreen })
                setTimeout(() => {
                    if (reloadHomepageFeed) {
                        router.reload()
                    }
                }, 500)
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'user.update')
                } else {
                    throw error
                }
            })
    }

    const initialSubjectOptions = issueSubjects.map((subject) => ({
        label: subject.name,
        data: subject,
    }))

    if ((!!issueSubjects?.length && !forceShow) || isPreventAccessDialogShown) return null

    return (
        <Container fullWidth={fullWidth}>
            {authenticatedUser ? (
                <Form ref={formRef} onSubmit={emptyFunction}>
                    <SubjectSelection
                        name="subjects"
                        label="subjects"
                        allowSubjectsFromDifferentLanguages
                        country={authenticatedUser.country.code}
                        initialSubject={initialSubjectOptions}
                        onSave={onSave}
                        title={t('app/yourIssueSubjectsLabel')}
                        text={t('app/subjectsInterestDialogText')}
                        openByDefault={hasUserSelectedIssueSubjects}
                        isDarkTheme
                    >
                        <FormElement />
                    </SubjectSelection>
                </Form>
            ) : null}
        </Container>
    )
}

export default IssueSubjectPicker
